import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from 'context/user/User';
import stationOrdersApi from 'api/requests/stationOrders';
import pusherService from 'services/pusher';
import styled, { keyframes } from 'styled-components';
import OrderCard from 'modules/restaurant/kitchen/OrderCard';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';

const COLUMNS = [
    { key: 'pending',   label: 'Pendente',   color: colors.occasionalOrange },
    { key: 'preparing', label: 'Preparando', color: colors.occasionalBlue || '#003393' },
    { key: 'ready',     label: 'Pronto',     color: colors.occasionalGreen },
    { key: 'delivered', label: 'Entregue',   color: colors.nightLight },
];

const POLL_INTERVAL = 30000;

// Simple hash → one of several accent colors so each station feels distinct
const STATION_COLORS = [
    colors.occasionalPurple,
    colors.occasionalOrange,
    colors.occasionalGreen,
    colors.primary,
    colors.occasionalPink,
];
function stationAccentColor(slug = '') {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) & 0xffffffff;
    return STATION_COLORS[Math.abs(h) % STATION_COLORS.length];
}

export default function StationPage({ station }) {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const establishmentId = user?.establishments?.[0]?.id;

    const [orders, setOrders] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [mobileTab, setMobileTab] = useState('pending');
    const [advancingIds, setAdvancingIds] = useState(new Set());
    const [isLive, setIsLive] = useState(false);
    const [logoutSheet, setLogoutSheet] = useState(false);

    const intervalRef = useRef(null);
    const inflightRef = useRef(new Set());
    const accentColor = stationAccentColor(station?.slug);

    // Fetch + poll
    useEffect(() => {
        if (!slug || !station?.id) return;
        let cancelled = false;

        function fetchOrders() {
            stationOrdersApi.list(slug, { station_id: station.id })
                .then(({ data }) => {
                    if (cancelled) return;
                    if (inflightRef.current.size === 0) {
                        setOrders(data.data || []);
                        setLastUpdated(new Date());
                    }
                })
                .catch(() => {});
        }

        fetchOrders();
        intervalRef.current = setInterval(fetchOrders, POLL_INTERVAL);
        return () => {
            cancelled = true;
            clearInterval(intervalRef.current);
        };
    }, [slug, station?.id]);

    // Pusher real-time
    const handleOrderCreated = useCallback((data) => {
        if (data.station_id !== station?.id) return;
        setOrders(prev => {
            if (prev.some(o => o.id === data.id)) return prev;
            return [data, ...prev];
        });
        setLastUpdated(new Date());
    }, [station?.id]);

    const handleStatusUpdated = useCallback((data) => {
        if (data.station_id !== station?.id) return;
        if (inflightRef.current.has(data.id)) return;
        setOrders(prev => prev.map(o =>
            o.id === data.id ? { ...o, status: data.status } : o
        ));
        setLastUpdated(new Date());
    }, [station?.id]);

    useEffect(() => {
        if (!establishmentId) return;

        const channelName = `establishments.${establishmentId}`;
        pusherService.subscribe(channelName, 'station_order.created', handleOrderCreated);
        pusherService.subscribe(channelName, 'station_order.status_updated', handleStatusUpdated);

        const channel = pusherService.channels[channelName];
        if (channel) {
            channel.bind('pusher:subscription_succeeded', () => setIsLive(true));
            channel.bind('pusher:subscription_error', () => setIsLive(false));
        }

        const connHandler = (state) => setIsLive(state.current === 'connected');
        pusherService.pusher.connection.bind('state_change', connHandler);

        if (pusherService.pusher.connection.state === 'connected' && channel) {
            setIsLive(true);
        }

        return () => {
            pusherService.pusher.connection.unbind('state_change', connHandler);
            pusherService.unsubscribe(channelName, 'station_order.created', handleOrderCreated);
            pusherService.unsubscribe(channelName, 'station_order.status_updated', handleStatusUpdated);
            setIsLive(false);
        };
    }, [establishmentId, handleOrderCreated, handleStatusUpdated]);

    async function handleAdvance(orderId, nextStatus) {
        if (!nextStatus) return;
        const order = orders.find(o => o.id === orderId);
        if (!order || advancingIds.has(orderId)) return;

        setAdvancingIds(prev => new Set([...prev, orderId]));
        setOrders(prev => prev.map(o =>
            o.id === orderId ? { ...o, status: nextStatus } : o
        ));

        inflightRef.current.add(orderId);
        try {
            await stationOrdersApi.updateStatus(slug, orderId, nextStatus);
        } catch {
            setOrders(prev => prev.map(o =>
                o.id === orderId ? { ...o, status: order.status } : o
            ));
        } finally {
            inflightRef.current.delete(orderId);
            setAdvancingIds(prev => { const s = new Set(prev); s.delete(orderId); return s; });
        }
    }

    function handleLogout() {
        localStorage.removeItem('otp_token');
        localStorage.removeItem('otp_user');
        window.location.href = '/acesso';
    }

    function getColumnOrders(status) {
        return orders
            .filter(o => o.status === status)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }

    const minutesAgo = Math.floor((Date.now() - lastUpdated.getTime()) / 60000);
    const pendingCount = getColumnOrders('pending').length;

    return (
        <Page>
            <PageHeader>
                <HeaderLeft>
                    <LogoutBtn onClick={() => setLogoutSheet(true)} title="Encerrar sessão">
                        ←
                    </LogoutBtn>
                    <StationBadge $color={accentColor}>{station?.name || 'Estação'}</StationBadge>
                    <UpdateBadge $live={isLive}>
                        {isLive ? 'Ao vivo' : minutesAgo === 0 ? 'Agora' : `${minutesAgo}min`}
                    </UpdateBadge>
                </HeaderLeft>
                <TotalBadge $urgent={pendingCount > 0}>
                    {orders.length} pedido{orders.length !== 1 ? 's' : ''}
                    {pendingCount > 0 && ` · ${pendingCount} pendente${pendingCount !== 1 ? 's' : ''}`}
                </TotalBadge>
            </PageHeader>

            {/* Mobile Tab Bar */}
            <MobileTabBar>
                {COLUMNS.map(col => (
                    <MobileTab
                        key={col.key}
                        $active={mobileTab === col.key}
                        $color={col.color}
                        onClick={() => setMobileTab(col.key)}
                    >
                        {col.label}
                        <MobileTabCount $active={mobileTab === col.key} $color={col.color}>
                            {getColumnOrders(col.key).length}
                        </MobileTabCount>
                    </MobileTab>
                ))}
            </MobileTabBar>

            {/* Mobile Content */}
            <MobileContent>
                {getColumnOrders(mobileTab).length === 0
                    ? <EmptyCol>Nenhum pedido {COLUMNS.find(c => c.key === mobileTab)?.label?.toLowerCase()}</EmptyCol>
                    : getColumnOrders(mobileTab).map(order => (
                        <OrderCard key={order.id} order={order} onAdvance={handleAdvance}
                            color={COLUMNS.find(c => c.key === order.status)?.color}
                            isAdvancing={advancingIds.has(order.id)} />
                    ))
                }
            </MobileContent>

            {/* Desktop Kanban */}
            <KanbanBoard>
                {COLUMNS.map(col => (
                    <KanbanColumn key={col.key}>
                        <ColumnHeader $color={col.color}>
                            <ColumnTitle>{col.label}</ColumnTitle>
                            <ColumnCount $color={col.color}>{getColumnOrders(col.key).length}</ColumnCount>
                        </ColumnHeader>
                        <ColumnCards>
                            {getColumnOrders(col.key).length === 0
                                ? <EmptyCol>Sem pedidos</EmptyCol>
                                : getColumnOrders(col.key).map(order => (
                                    <OrderCard key={order.id} order={order} onAdvance={handleAdvance}
                                        color={col.color} isAdvancing={advancingIds.has(order.id)} />
                                ))
                            }
                        </ColumnCards>
                    </KanbanColumn>
                ))}
            </KanbanBoard>

            {/* Logout confirm overlay */}
            {logoutSheet && (
                <LogoutOverlay onClick={() => setLogoutSheet(false)}>
                    <LogoutDialog onClick={e => e.stopPropagation()}>
                        <LogoutTitle>Encerrar sessão?</LogoutTitle>
                        <LogoutMessage>Você precisará inserir seu código novamente para acessar.</LogoutMessage>
                        <LogoutActions>
                            <LogoutCancel onClick={() => setLogoutSheet(false)}>Cancelar</LogoutCancel>
                            <LogoutConfirm onClick={handleLogout}>Encerrar</LogoutConfirm>
                        </LogoutActions>
                    </LogoutDialog>
                </LogoutOverlay>
            )}
        </Page>
    );
}

/* ─── Styles ─── */

const Page = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f0f0f3;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid ${colors.neutralDark};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`display: flex; align-items: center; gap: 10px;`;

const LogoutBtn = styled.button`
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: ${colors.neutralDark};
    border: none;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.night};
    flex-shrink: 0;
    transition: background 0.15s;
    &:hover { background: #e0e0e0; }
`;

const StationBadge = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    letter-spacing: -0.3px;
`;

const UpdateBadge = styled.span`
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 8px;
    color: ${({ $live }) => $live ? '#fff' : colors.nightLight};
    background: ${({ $live }) => $live ? colors.occasionalGreen : colors.neutralDark};
    transition: all 0.3s ease;
`;

const TotalBadge = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ $urgent }) => $urgent ? colors.occasionalOrange : colors.night};
`;

/* Mobile */
const MobileTabBar = styled.div`
    display: flex;
    background: #fff;
    border-bottom: 2px solid ${colors.neutralDark};
    flex-shrink: 0;
    @media screen and (min-width: ${breakpoints.smd}) { display: none; }
`;

const MobileTab = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 11px 8px;
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? '700' : '400'};
    color: ${({ $active, $color }) => $active ? $color : colors.nightLight};
    background: none;
    border: none;
    border-bottom: 2px solid ${({ $active, $color }) => $active ? $color : 'transparent'};
    margin-bottom: -2px;
    cursor: pointer;
    transition: color 0.15s;
`;

const MobileTabCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 600;
    background: ${({ $active, $color }) => $active ? $color : colors.neutralDark};
    color: ${({ $active }) => $active ? '#fff' : colors.nightLight};
    transition: all 0.15s;
`;

const MobileContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    @media screen and (min-width: ${breakpoints.smd}) { display: none; }
`;

/* Desktop Kanban */
const KanbanBoard = styled.div`
    display: none;
    @media screen and (min-width: ${breakpoints.smd}) {
        display: flex;
        flex: 1;
        overflow: hidden;
    }
`;

const KanbanColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    border-right: 1px solid ${colors.neutralDark};
    &:last-child { border-right: none; }
`;

const ColumnHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #fff;
    border-bottom: 3px solid ${({ $color }) => $color || colors.neutralDark};
    flex-shrink: 0;
`;

const ColumnTitle = styled.span`font-weight: 600; font-size: 15px; color: ${colors.nightDark};`;
const ColumnCount = styled.span`
    background: ${({ $color }) => $color || colors.neutralDark}25;
    color: ${({ $color }) => $color || colors.nightDark};
    border-radius: 10px;
    padding: 2px 10px;
    font-size: 13px;
    font-weight: 600;
`;

const ColumnCards = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: #f0f0f3;
`;

const EmptyCol = styled.div`
    text-align: center;
    padding: 32px 0;
    color: ${colors.nightLight};
    font-size: 14px;
`;

/* Logout confirm */
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const LogoutOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 20px;
    animation: ${fadeIn} 0.15s ease;
`;

const LogoutDialog = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 24px;
    width: 100%;
    max-width: 320px;
    animation: ${slideUp} 0.2s ease;
`;

const LogoutTitle = styled.h3`font-size: 17px; font-weight: 600; color: ${colors.nightDark}; margin: 0 0 8px;`;
const LogoutMessage = styled.p`font-size: 14px; color: ${colors.night}; margin: 0 0 20px; line-height: 1.5;`;
const LogoutActions = styled.div`display: flex; gap: 10px;`;
const LogoutCancel = styled.button`
    flex: 1; height: 44px; border-radius: 10px; border: 1.5px solid ${colors.neutralDark};
    background: #fff; font-size: 15px; font-weight: 600; color: ${colors.night}; cursor: pointer;
`;
const LogoutConfirm = styled.button`
    flex: 1; height: 44px; border-radius: 10px; border: none;
    background: ${colors.danger}; color: #fff; font-size: 15px; font-weight: 600; cursor: pointer;
`;
